import { Calendar } from "lucide-react";

/**
 * Timeframe options for filtering insights & KPIs.
 *  - day       : today only
 *  - week      : last 7 days
 *  - month     : last 30 days (default)
 *  - quarter   : last 90 days
 *  - year      : last 365 days
 *  - fy        : Australian FY (Jul 1 → Jun 30)
 */
export const TIMEFRAMES = [
  { id: "day", label: "Day", short: "1D", days: 1 },
  { id: "week", label: "Week", short: "7D", days: 7 },
  { id: "month", label: "Month", short: "30D", days: 30 },
  { id: "quarter", label: "Quarter", short: "90D", days: 90 },
  { id: "year", label: "Year", short: "365D", days: 365 },
  { id: "fy", label: "FY", short: "FY", days: null },
];

/**
 * Returns { start: Date, end: Date, days: number, label: string, id }
 * for the given timeframe id.
 */
export function resolveTimeframe(id) {
  const now = new Date();
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);

  if (id === "fy") {
    // AU FY: Jul 1 -> Jun 30
    const y = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
    const start = new Date(y, 6, 1, 0, 0, 0, 0);
    const days = Math.max(1, Math.round((end - start) / 86400000));
    return { id, start, end, days, label: `FY${String(y + 1).slice(2)}`, longLabel: `FY${y}/${String(y + 1).slice(2)}` };
  }
  const tf = TIMEFRAMES.find((t) => t.id === id) || TIMEFRAMES[2];
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - (tf.days - 1));
  return { id: tf.id, start, end, days: tf.days, label: tf.label, longLabel: tf.label };
}

/** Filter an array of items to those whose date field falls within the timeframe. */
export function filterInTimeframe(items, tf, dateFields = ["created_at", "reported_at", "ts", "date"]) {
  if (!Array.isArray(items) || !tf) return [];
  const s = tf.start.getTime();
  const e = tf.end.getTime();
  return items.filter((it) => {
    const raw = dateFields.map((f) => it?.[f]).find(Boolean);
    if (!raw) return false;
    const t = new Date(raw).getTime();
    return !isNaN(t) && t >= s && t <= e;
  });
}

export default function TimeframePicker({ value, onChange, testid = "timeframe-picker" }) {
  return (
    <div
      className="inline-flex items-center gap-1 p-1 rounded-xl"
      style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 px-2 text-muted">
        <Calendar className="w-3.5 h-3.5" />
        <span className="text-[10px] uppercase tracking-widest font-black">Range</span>
      </div>
      {TIMEFRAMES.map((t) => {
        const active = t.id === value;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all duration-150 ${
              active ? "text-black scale-105 shadow-lg shadow-orange-500/30" : "text-white/70 hover:text-white hover:bg-white/5"
            }`}
            style={active ? { background: "var(--primary)" } : {}}
            data-testid={`${testid}-${t.id}`}
            title={t.label}
          >
            {t.short}
          </button>
        );
      })}
    </div>
  );
}
